import { useRouter } from "next/router";

import { Card } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";
import {
  convertFromBillingToUpdateBillingAgreement,
  useBillingQuery,
  useCreateAddressMutation,
  useUpdateBillingAgreementMutation,
} from "@chef/state-management";
import { AppShell } from "@chef/feature-app-shell";

import { MyAccountLayout } from "../../components/MyAccountLayout";
import { AddressForm, AddressFormValues } from "../../components/AddressForm";

import { intl } from "./MyAccountNewAddressPage.Intl";

export const MyAccountNewAddressPage = () => {
  const router = useRouter();

  const [createAddressMutation, { isLoading: isCreatingAddress }] =
    useCreateAddressMutation();

  const [updateBillingAgreementMutation, { isLoading: isUpdatingBilling }] =
    useUpdateBillingAgreementMutation();

  const { data: billingQuery, refetch: refetchBilling } = useBillingQuery();

  const isLoading = isCreatingAddress || isUpdatingBilling;

  const billing = billingQuery?.billing;

  const createAddress = async (address: AddressFormValues) => {
    try {
      const { id } = await createAddressMutation({
        streetAddress: address.streetAddress,
        postalCode: address.postalCode,
        latitude: address.validatedAddress.latitude,
        longitude: address.validatedAddress.longitude,
        postalArea: address.validatedAddress.postalArea,
        addressName: address.validatedAddress.addressName,
        streetName: address.validatedAddress.streetName,
        streetNumber: address.validatedAddress.streetNumber,
        letterExtra: address.validatedAddress.letterExtra,
        addressCode: address.validatedAddress.addressCode,
        addressUUID: address.validatedAddress.addressGuid,
        apartmentName: address.apartment.name,
        floor: address.apartment.floor,
        door: address.apartment.door || undefined,
        setAsBilling: false,
        validated:
          address.validatedAddress.mode === 3 ? new Date().toUTCString() : "",
        addressDigitalKeyProviderId:
          address.validatedAddress.digitalKeyProvider
            ?.addressDigitalKeyProviderId,
        instructions:
          address.instructionsToDriver !== null
            ? address.instructionsToDriver
            : undefined,
        alias: address.name || address.streetAddress,
        requiresPhysicalKey: address.requiresPhysicalKey || undefined,
      })
        .unwrap()
        .catch(() => {
          throw new Error("Address not registered");
        });

      if (
        billing &&
        address.setAsNewShippingAddress &&
        address.basketTimeblockId
      ) {
        await updateBillingAgreementMutation(
          convertFromBillingToUpdateBillingAgreement({
            ...billing,
            baskets: billing.baskets.map((b) => {
              if (!b.default) {
                return b;
              }

              return {
                ...b,
                shippingAddressId: id,
                timeblockId: address.basketTimeblockId || b.timeblockId,
                deliveryInterval:
                  address.basketDeliveryWeekType || b.deliveryInterval,
              };
            }),
          }),
        )
          .unwrap()
          .catch(() => {
            throw new Error("Billing update failed");
          });

        // Force refetch billing to update the state
        await refetchBilling();
      }

      router.push(BRAND_PATHS.DELIVERY_INFORMATION_PAGE);
    } catch (err) {
      if (err instanceof Error && err.message === "Address not registered") {
        console.error("Error adding address", err);
      }
      if (err instanceof Error && err.message === "Billing update failed") {
        console.error("Error updating billing agreement", err);
      }
    }
  };

  return (
    <AppShell title={intl.ADD_ADDRESS_TITLE}>
      <AppShell.Meta />
      <AppShell.Header />
      <AppShell.AccountPausedBanner />
      <AppShell.OldVippsBanner />

      <AppShell.Main grid>
        <MyAccountLayout
          page="deliveryInformation"
          title={intl.ADD_ADDRESS}
          hideBackOnMobile
        >
          <Card className="-mx-4 -mt-10 md:m-0 col-span-full md:col-span-5">
            <AddressForm
              onFormSubmitted={createAddress}
              isLoading={isLoading}
            />
          </Card>
        </MyAccountLayout>
      </AppShell.Main>
      <AppShell.TabBar />
    </AppShell>
  );
};
